import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/shared/integrations/supabase/client';
import { preloadGoogleMaps } from '@/shared/lib/googleMapsLoader';
import { fetchAuthorizedPropertyRefIds } from '@/features/projects/services/projectsApi';
import { isAdminUser } from '@/shared/lib/adminUids';
import { firebaseAuth } from '@/shared/integrations/firebase/client';

// Chunk large id arrays to keep Supabase IN() queries within URL length limits.
const chunkIds = <T,>(arr: T[], size: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

interface PropertyAnalytics {
  id: string;
  name: string;
  address: string;
  reportsCount: number;
  expectedReports: number;
  completionPercentage: number;
  status: 'completed' | 'in_progress' | 'pending';
  lastUpdated: string;
  criticalMissing: string[];
  primaryPhotoUrl?: string;
  assetCount?: number;
  city?: string;
  state?: string;
  created_at?: string;
  latitude?: number | null;
  longitude?: number | null;
  // Retail-specific fields
  store_number?: string | null;
}

interface ClientData {
  properties: PropertyAnalytics[];
  totalAssets: number;
  totalReports: number;
  overallCompletion: number;
  completedProperties: number;
  inProgressProperties: number;
  pendingProperties: number;
  criticalAlerts: number;
  avgCompliance: number;
  client?: {
    id: string;
    name: string;
    description?: string | null;
    logo_url?: string | null;
    client_branding?: any;
  };
}

const ClientDataContext = createContext<{
  data: ClientData | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}>({
  data: null,
  loading: true,
  error: null,
  refetch: async () => {}
});

export function ClientDataProvider({ 
  clientId, 
  children 
}: { 
  clientId: string; 
  children: ReactNode;
}) {
  const [data, setData] = useState<ClientData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchClientData = async () => {
    if (!clientId) return;
    
    setLoading(true);
    setError(null);
    
    // Preload Google Maps in parallel (non-blocking)
    preloadGoogleMaps();
    
    try {
      // 1. Fetch client/project info
      // NOTE: client_branding column doesn't exist on prod yet (schema drift).
      // Dropped from the select until a migration adds it; the field is
      // optional in the resulting type so downstream consumers get undefined.
      const { data: projectData, error: projectError } = await supabase
        .from('projects')
        .select('id, name, description, logo_url')
        .eq('id', clientId)
        .single();
      
      if (projectError) throw projectError;

      // 2. Fetch the client's properties.
      //    - Client users: the authorized set from the /projects/client endpoint
      //      (token-scoped to their org), loaded from Supabase by ref_id.
      //    - Admins: the SELECTED client's properties directly by organization_id
      //      (so they can switch between all clients, like before). NOTE: relies
      //      on the organization_id linkage; rows with null organization_id won't
      //      appear until that backfill lands.
      // NOTE: primary_photo_id, team_id, mall_name, division_name, market,
      // geo_region, phone, combo_store, location_type columns don't exist on
      // prod (schema drift); kept out of the select. Optional fields in
      // PropertyAnalytics degrade to undefined.
      const PROP_SELECT = 'id, name, address, city, state, created_at, latitude, longitude, store_number';
      let properties: any[] = [];
      if (isAdminUser(firebaseAuth?.currentUser?.uid)) {
        const pageSize = 1000;
        let from = 0;
        while (true) {
          const { data: propBatch, error: propertiesError } = await supabase
            .from('properties')
            .select(PROP_SELECT)
            .eq('organization_id', clientId)
            .range(from, from + pageSize - 1);
          if (propertiesError) throw propertiesError;
          if (propBatch?.length) properties.push(...propBatch);
          if (!propBatch || propBatch.length < pageSize) break;
          from += pageSize;
        }
      } else {
        const refIds = await fetchAuthorizedPropertyRefIds();
        for (const idChunk of chunkIds(refIds, 200)) {
          const { data: propBatch, error: propertiesError } = await supabase
            .from('properties')
            .select(PROP_SELECT)
            // Pin to the caller's org: the ref_id set can span orgs (unsynced
            // duplicate rows share a ref_id), so ref_id alone over-counts.
            .in('ref_id', idChunk)
            .eq('organization_id', clientId);
          if (propertiesError) throw propertiesError;
          if (propBatch?.length) properties.push(...propBatch);
        }
      }

      if (!properties?.length) {
        setData({
          properties: [],
          totalAssets: 0,
          totalReports: 0,
          overallCompletion: 0,
          completedProperties: 0,
          inProgressProperties: 0,
          pendingProperties: 0,
          criticalAlerts: 0,
          avgCompliance: 0,
          client: projectData
        });
        setLoading(false);
        return;
      }

      const propertyIds = properties.map(p => p.id);

      // Keep IN() queries within safe URL size limits
      const propertyIdChunks = chunkIds(propertyIds, 200);

      // 4. Fetch ALL assets with pagination + chunking (ONCE)
      const allAssets: any[] = [];
      const assetPageSize = 1000;

      for (const idChunk of propertyIdChunks) {
        let assetFrom = 0;
        while (true) {
          // NOTE: `category` and `manufacture_date` columns don't exist on
          // prod — split into `category_id` (FK to asset_categories) +
          // `category_attributes` (jsonb) and renamed to
          // `manufacture_date_string` (text), respectively. PostgREST returns
          // 400 on any SELECT that names a non-existent column, so the prior
          // base-app shape silently blanked the client name on the dashboard
          // route. Dropped from the select; downstream consumers using these
          // fields degrade to undefined. Follow-up to the d71ba7f
          // `properties`-table fix — that pass explicitly punted
          // property_assets drift (docs/base_app_schema_drift_fix_plan.md §A
          // "Other gotchas"), but this query IS on the dashboard path via
          // ClientLayoutWithSidebar → ClientDataProvider.
          const { data: assetBatch, error: assetError } = await supabase
            .from('property_assets')
            .select('id, property_id, created_at')
            .in('property_id', idChunk)
            .range(assetFrom, assetFrom + assetPageSize - 1);

          if (assetError) throw assetError;
          if (assetBatch?.length) allAssets.push(...assetBatch);
          if (!assetBatch || assetBatch.length < assetPageSize) break;
          assetFrom += assetPageSize;
        }
      }

      // 5. Fetch documents (reports) in chunks
      const documents: Array<{ property_id: string | null; document_type: string; created_at: string }> = [];
      for (const idChunk of propertyIdChunks) {
        const { data, error } = await supabase
          .from('documents')
          .select('property_id, document_type, created_at')
          .in('property_id', idChunk);

        if (error) throw error;
        if (data?.length) documents.push(...data);
      }

      // 7. Process analytics for each property
      const propertyAnalytics: PropertyAnalytics[] = properties.map(property => {
        const assetCount = allAssets.filter(a => a.property_id === property.id).length;
        const reportsCount = documents.filter(d => d.property_id === property.id).length;

        const expectedReports = 10;
        const completionPercentage = Math.min(Math.round((reportsCount / expectedReports) * 100), 100);

        let status: 'completed' | 'in_progress' | 'pending';
        if (completionPercentage >= 90) status = 'completed';
        else if (completionPercentage >= 50) status = 'in_progress';
        else status = 'pending';

        const criticalMissing: string[] = [];
        if (reportsCount < 3) criticalMissing.push('Property Reports');

        const lastUpdated = documents
          .filter(d => d.property_id === property.id)
          .map(d => d.created_at)
          .sort()
          .reverse()[0] || property.created_at;

        return {
          id: property.id,
          name: property.name,
          address: `${property.address || ''} ${property.city || ''} ${property.state || ''}`.trim(),
          reportsCount,
          expectedReports,
          completionPercentage,
          status,
          lastUpdated,
          criticalMissing,
          assetCount,
          city: property.city,
          state: property.state,
          created_at: property.created_at,
          latitude: property.latitude,
          longitude: property.longitude,
          // Retail-specific fields
          store_number: property.store_number
        };
      });

      // 8. Calculate overall analytics
      const totalAssets = propertyAnalytics.reduce((sum, p) => sum + (p.assetCount || 0), 0);
      const totalReports = propertyAnalytics.reduce((sum, p) => sum + p.reportsCount, 0);
      const overallCompletion = propertyAnalytics.length > 0
        ? Math.round(propertyAnalytics.reduce((sum, p) => sum + p.completionPercentage, 0) / propertyAnalytics.length)
        : 0;
      
      // MVP2-282: Calculate compliance based on asset health conditions (non-critical assets)
      let avgCompliance = 0;
      if (totalAssets > 0) {
        // Import getAgeBasedCondition function
        const { getAgeBasedCondition } = await import('@/features/assets/services/assetConditionUtils');
        
        // Count critical assets based on manufacture date
        const criticalAssets = allAssets.filter(asset => {
          // Use manufacture_date for condition assessment
          const conditionResult = getAgeBasedCondition(
            asset.manufacture_date, 
            null // We don't have manufacture_date_custom here, use null
          );
          return conditionResult.condition === 'critical';
        }).length;
        
        // Compliance = percentage of non-critical assets
        avgCompliance = Math.round(((totalAssets - criticalAssets) / totalAssets) * 100);
        
      }
      const completedProperties = propertyAnalytics.filter(p => p.status === 'completed').length;
      const inProgressProperties = propertyAnalytics.filter(p => p.status === 'in_progress').length;
      const pendingProperties = propertyAnalytics.filter(p => p.status === 'pending').length;
      const criticalAlerts = propertyAnalytics.reduce((sum, p) => sum + p.criticalMissing.length, 0);

      setData({
        properties: propertyAnalytics,
        totalAssets,
        totalReports,
        overallCompletion,
        completedProperties,
        inProgressProperties,
        pendingProperties,
        criticalAlerts,
        avgCompliance,
        client: projectData
      });

    } catch (err) {
      console.error('[ClientDataProvider] Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClientData();
  }, [clientId]);

  return (
    <ClientDataContext.Provider value={{ data, loading, error, refetch: fetchClientData }}>
      {children}
    </ClientDataContext.Provider>
  );
}

export function useClientData() {
  return useContext(ClientDataContext);
}
